export type PlanningPhase = 'thinking' | 'reasoning' | 'planning' | 'steps' | 'code';

export interface ParsedPlan {
  thinking: string;
  reasoning: string;
  planning: string;
  steps: string[];
  code: string;
}

export const initialPlan: ParsedPlan = {
  thinking: '',
  reasoning: '',
  planning: '',
  steps: [],
  code: '',
};

const MARKERS = {
  THINKING: '[THINKING]',
  REASONING: '[REASONING]',
  PLANNING: '[PLANNING]',
  STEPS: '[STEPS]',
  CODE: '[CODE]',
};

export function parseStreamingPlan(content: string): {
  plan: ParsedPlan;
  activePhase: PlanningPhase | null;
} {
  const plan = { ...initialPlan };
  let activePhase: PlanningPhase | null = null;

  // Simple parser that splits by markers
  // Note: This assumes markers appear in order or at least don't nest weirdly

  let currentContent = content;
  let currentPhase: PlanningPhase | null = null;

  // Find all marker positions
  const positions = [
    { phase: 'thinking' as const, index: currentContent.indexOf(MARKERS.THINKING) },
    { phase: 'reasoning' as const, index: currentContent.indexOf(MARKERS.REASONING) },
    { phase: 'planning' as const, index: currentContent.indexOf(MARKERS.PLANNING) },
    { phase: 'steps' as const, index: currentContent.indexOf(MARKERS.STEPS) },
    { phase: 'code' as const, index: currentContent.indexOf(MARKERS.CODE) },
  ]
    .filter((p) => p.index !== -1)
    .sort((a, b) => a.index - b.index);

  if (positions.length === 0) {
    // No markers yet, assume thinking if content exists, or just waiting
    if (content.trim()) {
      plan.thinking = content;
      activePhase = 'thinking';
    }
    return { plan, activePhase };
  }

  // Process segments
  for (let i = 0; i < positions.length; i++) {
    const current = positions[i];
    const next = positions[i + 1];

    const start =
      current.index + MARKERS[current.phase.toUpperCase() as keyof typeof MARKERS].length;
    const end = next ? next.index : content.length;

    const segmentContent = content.substring(start, end).trim();

    if (current.phase === 'steps') {
      // Parse steps by newlines or bullets
      plan.steps = segmentContent
        .split('\n')
        .map((line) => line.replace(/^[-*•\d.]+\s+/, '').trim())
        .filter((line) => line.length > 0);
    } else {
      plan[current.phase] = segmentContent;
    }

    // The last found marker is the active phase
    if (i === positions.length - 1) {
      activePhase = current.phase;
    }
  }

  return { plan, activePhase };
}
